import { Children, cloneElement, isValidElement, useId } from 'react'
import type { ComponentProps, InputHTMLAttributes } from 'react'
import type { Options } from 'react-markdown'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Highlighted, SmallTitle, Title } from './Text'
import { classNames } from '~/utils/client/helpers'
import { Link } from '@remix-run/react'

type MarkdownListItemProps = ComponentProps<'li'> & {
  node?: unknown
}

function MarkdownListItem({ className, children, node, ...props }: MarkdownListItemProps) {
  const childrenArray = Children.toArray(children)
  const [checkbox, ...labelContent] = childrenArray
  const labelId = useId()
  const isTaskListItem =
    typeof className === 'string' && className.split(' ').includes('task-list-item')

  if (!isTaskListItem) {
    return (
      <li className={className} {...props}>
        {children}
      </li>
    )
  }

  return (
    <li
      className={classNames('flex min-w-0 items-baseline gap-2 break-words', className)}
      {...props}
    >
      {isValidElement(checkbox)
        ? cloneElement(checkbox, {
            'aria-labelledby': labelId,
          } as InputHTMLAttributes<HTMLInputElement>)
        : checkbox}
      <span id={labelId} className="min-w-0 break-words">
        {labelContent}
      </span>
    </li>
  )
}

export const CustomMarkdown = ({ children, className, ...props }: Readonly<Options>) => {
  return (
    <Markdown
      components={{
        h1(props) {
          return <Title className="leading-10 mb-2" {...props} />
        },
        h2(props) {
          return <SmallTitle className="leading-10 mb-1" {...props} />
        },
        h3(props) {
          return <SmallTitle className="text-lg leading-8" {...props} />
        },
        h4(props) {
          return <Highlighted className="leading-6" {...props} />
        },
        a(props) {
          return (
            // @ts-ignore
            <Link
              to={props.href?.toString() ?? ''}
              className="text-accent focus-ring-brand-inset-stack rounded-sm p-1"
              {...props}
            >
              {props.children}
            </Link>
          )
        },
        ul(props) {
          return (
            <ul className="list-disc list-outside break-words pl-5" {...props}>
              {props.children}
            </ul>
          )
        },
        ol(props) {
          return (
            <ol className="list-decimal list-outside break-words pl-5" {...props}>
              {props.children}
            </ol>
          )
        },
        p(props) {
          return <p className="break-words" {...props} />
        },
        pre(props) {
          return (
            <pre
              className="max-w-full overflow-x-auto whitespace-pre-wrap break-words"
              {...props}
            />
          )
        },
        table({ children, ...props }) {
          return (
            <div className="max-w-full overflow-x-auto">
              <table className="w-full min-w-0" {...props}>
                {children}
              </table>
            </div>
          )
        },
        img(props) {
          return <img className="max-w-full h-auto" {...props} />
        },
        li: MarkdownListItem,
      }}
      remarkPlugins={[[remarkGfm]]}
      className={classNames('max-w-full min-w-0 break-words [overflow-wrap:anywhere]', className)}
      {...props}
    >
      {children}
    </Markdown>
  )
}
